// ============================================================================
// AUTOMATIC MASTER CODES — next-code preview for the customer and service forms,
// and the prefix / digit-width settings on the organisation screen.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { masterCodeService, partyService, serviceService } from 'api/services';
import type { MasterCodeSeriesDto, UpdateMasterCodeSeriesRequest } from 'api/types';

/**
 * The code the next create would receive, shown as the field's placeholder. Loaded only while
 * `enabled` (a create or copy form). Null while loading or when the preview is unavailable.
 */
const useNextCode = (enabled: boolean, load: () => Promise<string>, deps: unknown[]): string | null => {
  const [code, setCode] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) { setCode(null); return; }
    let cancelled = false;
    load()
      .then(next => { if (!cancelled) setCode(next); })
      .catch(() => { if (!cancelled) setCode(null); });
    return () => { cancelled = true; };
    // `load` is rebuilt each render; the caller's deps say when the preview must refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
  return code;
};

export const useNextPartyCode = (enabled: boolean): string | null =>
  useNextCode(enabled, () => partyService.getNextCode(), []);

export const useNextServiceCode = (enabled: boolean, itemType: string): string | null =>
  useNextCode(enabled, () => serviceService.getNextCode(itemType), [itemType]);

/** Every code series of the entity, with save. Used by the organisation settings card. */
export const useMasterCodeSeries = () => {
  const [series, setSeries] = useState<MasterCodeSeriesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setSeries(await masterCodeService.getSeries());
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async (seriesKey: string, data: UpdateMasterCodeSeriesRequest) => {
    const saved = await masterCodeService.saveSeries(seriesKey, data);
    setSeries(prev => prev.map(s => (s.seriesKey === seriesKey ? saved : s)));
    return saved;
  }, []);

  return { series, loading, error, reload, save };
};
